// Pure ICAO 9303 machine-readable-zone helpers for the free MRZ tools and for
// the example zones the programmatic country pages publish.
//
// All three zone formats are generated here: TD3, two lines of 44, the one a
// passport carries; TD2, two lines of 36; and TD1, three lines of 30. Reading a
// pasted zone of any of them is ./mrz-parser, which uses the check digit
// below. Nothing here does any I/O or touches the DOM, so it is unit-tested
// directly and runs unchanged in the browser. Every value a generator produces
// is synthetic: the caller supplies the inputs and no real holder data is
// embedded.

export const TD3_LINE_LENGTH = 44;

// The checked value of a character is its digit, A-Z as 10-35, and the filler
// `<` as 0. The position weights repeat 7, 3, 1 across the input.
function weightAt(index: number): number {
  const cycle = index % 3;
  return cycle === 0 ? 7 : cycle === 1 ? 3 : 1;
}

function charValue(ch: string): number {
  if (ch >= "0" && ch <= "9") {
    return ch.charCodeAt(0) - 48;
  }
  if (ch >= "A" && ch <= "Z") {
    return ch.charCodeAt(0) - 55; // 'A' -> 10
  }
  if (ch === "<") {
    return 0;
  }
  return -1; // signals an illegal character to the caller
}

/**
 * The ICAO 9303 check digit of a field, or null when the field contains a
 * character that is not A-Z, 0-9 or `<`.
 */
export function checkDigit(field: string): number | null {
  let sum = 0;
  for (let i = 0; i < field.length; i++) {
    const value = charValue(field.charAt(i));
    if (value < 0) {
      return null;
    }
    sum += value * weightAt(i);
  }
  return sum % 10;
}

/** One character's contribution to a check digit, as the arithmetic shows it. */
export interface CheckDigitStep {
  readonly char: string;
  /** The character's checked value: its digit, A-Z as 10-35, `<` as 0. */
  readonly value: number;
  /** The repeating position weight, 7, 3 or 1. */
  readonly weight: number;
  readonly product: number;
}

/** A check digit with the arithmetic that produced it. */
export interface CheckDigitWorking {
  readonly field: string;
  readonly steps: readonly CheckDigitStep[];
  readonly sum: number;
  readonly digit: number;
}

/**
 * The same computation as `checkDigit`, with every intermediate value kept.
 *
 * It exists so a page can SHOW the arithmetic rather than describe it: an
 * explainer that retyped the products would be a second implementation to keep
 * true, and the first thing a reader checking our worked example would find
 * wrong. Throws on a character outside the zone's alphabet, which is the case
 * `checkDigit` reports as null — a caller building an explanation supplies its
 * own field and has nothing to recover from.
 */
export function checkDigitSteps(field: string): CheckDigitWorking {
  const steps: CheckDigitStep[] = [];
  let sum = 0;
  for (let i = 0; i < field.length; i++) {
    const char = field.charAt(i);
    const value = charValue(char);
    if (value < 0) throw new Error(`not a machine-readable field: ${field}`);
    const weight = weightAt(i);
    const product = value * weight;
    steps.push({ char, value, weight, product });
    sum += product;
  }
  return { field, steps, sum, digit: sum % 10 };
}

// Uppercases, keeps only A-Z and digits, and turns every run of other
// characters into the filler `<`.
function normalizeName(part: string): string {
  return part
    .toUpperCase()
    .normalize("NFKD")
    .replace(/[^A-Z0-9]+/g, "<")
    .replace(/^<+|<+$/g, "");
}

function fill(value: string, length: number): string {
  const clipped = value.slice(0, length);
  return clipped + "<".repeat(length - clipped.length);
}

export type Sex = "M" | "F" | "X";

// The check digit of a field the generators built. Everything they hand it has
// already been filtered to the zone's own alphabet, so a null here is a bug in
// this module rather than bad input — and a zone carrying a silently
// substituted digit is worse than no zone at all, because it verifies as wrong.
// The null case belongs to `checkDigit` itself, which is also the parser's, and
// the parser takes strings it did not produce.
function digitFor(field: string): number {
  const digit = checkDigit(field);
  if (digit === null) throw new Error(`not a machine-readable field: ${field}`);
  return digit;
}

export interface Td3Input {
  /** Single-letter document type; `P` for a passport. */
  readonly documentType: string;
  /** Issuing state, ISO 3166-1 alpha-3. */
  readonly issuingCountry: string;
  readonly surname: string;
  readonly givenNames: string;
  /** Document (passport) number, up to 9 characters. */
  readonly documentNumber: string;
  /** Holder nationality, ISO 3166-1 alpha-3. */
  readonly nationality: string;
  /** Date of birth as YYMMDD. */
  readonly dateOfBirth: string;
  readonly sex: Sex;
  /** Expiry date as YYMMDD. */
  readonly expiryDate: string;
  /** Optional personal number, up to 14 characters. */
  readonly personalNumber?: string;
}

// The name zone is 39 characters: SURNAME, the separator `<<`, then the given
// names, everything padded with the filler.
function nameZone(surname: string, givenNames: string): string {
  const surnamePart = normalizeName(surname);
  const givenPart = normalizeName(givenNames);
  const composed = givenPart ? `${surnamePart}<<${givenPart}` : surnamePart;
  return fill(composed, 39);
}

function code3(value: string): string {
  return fill(value.toUpperCase().replace(/[^A-Z]/g, ""), 3);
}

function sexChar(sex: Sex): string {
  return sex === "X" ? "<" : sex;
}

export interface Td3Lines {
  readonly line1: string;
  readonly line2: string;
}

/** Builds a valid TD3 machine-readable zone from synthetic input. */
export function generateTd3(input: Td3Input): Td3Lines {
  const type = fill(input.documentType.toUpperCase().replace(/[^A-Z<]/g, ""), 2);
  const line1 = fill(
    `${type}${code3(input.issuingCountry)}${nameZone(input.surname, input.givenNames)}`,
    TD3_LINE_LENGTH,
  );

  const documentNumber = fill(input.documentNumber.toUpperCase().replace(/[^A-Z0-9]/g, ""), 9);
  const documentCheck = digitFor(documentNumber);
  const birth = fill(input.dateOfBirth.replace(/[^0-9]/g, ""), 6);
  const birthCheck = digitFor(birth);
  const expiry = fill(input.expiryDate.replace(/[^0-9]/g, ""), 6);
  const expiryCheck = digitFor(expiry);
  const personal = fill((input.personalNumber ?? "").toUpperCase().replace(/[^A-Z0-9]/g, ""), 14);
  const personalCheck = digitFor(personal);

  const composite = `${documentNumber}${documentCheck}${birth}${birthCheck}${expiry}${expiryCheck}${personal}${personalCheck}`;
  const compositeCheck = digitFor(composite);

  const line2 = `${documentNumber}${documentCheck}${code3(input.nationality)}${birth}${birthCheck}${sexChar(input.sex)}${expiry}${expiryCheck}${personal}${personalCheck}${compositeCheck}`;

  return { line1, line2: fill(line2, TD3_LINE_LENGTH) };
}

export const TD2_LINE_LENGTH = 36;
export const TD1_LINE_LENGTH = 30;

// TD1 and TD2 carry the same data as TD3 in narrower lines, so the input is the
// same with one addition: both formats have an optional-data field the issuing
// state fills as it likes, and TD1 has two of them. The personal number of a
// TD3 zone has no fixed counterpart — on TD1 it is conventionally written into
// the first optional field — so it is the caller that decides what goes there.
export interface Td2Input extends Omit<Td3Input, "personalNumber"> {
  /** Optional data, 7 characters on TD2; the issuing state decides its meaning. */
  readonly optionalData?: string;
}

export interface Td1Input extends Omit<Td3Input, "personalNumber"> {
  /** Optional data on the upper line, 15 characters. */
  readonly optionalData?: string;
  /** Optional data on the middle line, 11 characters. */
  readonly optionalData2?: string;
}

export interface Td2Lines {
  readonly line1: string;
  readonly line2: string;
}

export interface Td1Lines {
  readonly line1: string;
  readonly line2: string;
  readonly line3: string;
}

/**
 * Builds a valid TD2 machine-readable zone (two lines of 36) from synthetic
 * input, per ICAO 9303 part 6. The name zone is 31 characters here rather than
 * TD3's 39, so a long name is truncated sooner — which is the format's own
 * behaviour, not a defect of this generator.
 */
export function generateTd2(input: Td2Input): Td2Lines {
  const type = fill(input.documentType.toUpperCase().replace(/[^A-Z<]/g, ""), 2);
  const line1 = fill(
    `${type}${code3(input.issuingCountry)}${fill(nameZone(input.surname, input.givenNames), 31)}`,
    TD2_LINE_LENGTH,
  );

  const documentNumber = fill(input.documentNumber.toUpperCase().replace(/[^A-Z0-9]/g, ""), 9);
  const documentCheck = digitFor(documentNumber);
  const birth = fill(input.dateOfBirth.replace(/[^0-9]/g, ""), 6);
  const birthCheck = digitFor(birth);
  const expiry = fill(input.expiryDate.replace(/[^0-9]/g, ""), 6);
  const expiryCheck = digitFor(expiry);
  const optional = fill((input.optionalData ?? "").toUpperCase().replace(/[^A-Z0-9]/g, ""), 7);

  // The composite covers the lower line except the nationality and the sex,
  // which is why an issuing state can be changed in a zone without any digit
  // noticing.
  const composite = `${documentNumber}${documentCheck}${birth}${birthCheck}${expiry}${expiryCheck}${optional}`;
  const compositeCheck = digitFor(composite);

  const line2 = `${documentNumber}${documentCheck}${code3(input.nationality)}${birth}${birthCheck}${sexChar(input.sex)}${expiry}${expiryCheck}${optional}${compositeCheck}`;

  return { line1, line2: fill(line2, TD2_LINE_LENGTH) };
}

/**
 * Builds a valid TD1 machine-readable zone (three lines of 30) from synthetic
 * input, per ICAO 9303 part 5. The name has a line to itself here, so it is the
 * only one of the three formats where the name zone is not shared with the
 * document code and the issuing state.
 */
export function generateTd1(input: Td1Input): Td1Lines {
  const type = fill(input.documentType.toUpperCase().replace(/[^A-Z<]/g, ""), 2);
  const documentNumber = fill(input.documentNumber.toUpperCase().replace(/[^A-Z0-9]/g, ""), 9);
  const documentCheck = digitFor(documentNumber);
  const optional = fill((input.optionalData ?? "").toUpperCase().replace(/[^A-Z0-9]/g, ""), 15);
  const line1 = fill(
    `${type}${code3(input.issuingCountry)}${documentNumber}${documentCheck}${optional}`,
    TD1_LINE_LENGTH,
  );

  const birth = fill(input.dateOfBirth.replace(/[^0-9]/g, ""), 6);
  const birthCheck = digitFor(birth);
  const expiry = fill(input.expiryDate.replace(/[^0-9]/g, ""), 6);
  const expiryCheck = digitFor(expiry);
  const optional2 = fill((input.optionalData2 ?? "").toUpperCase().replace(/[^A-Z0-9]/g, ""), 11);

  const middle = `${birth}${birthCheck}${sexChar(input.sex)}${expiry}${expiryCheck}${code3(input.nationality)}${optional2}`;

  // The composite runs over positions 6-30 of the upper line and 1-7, 9-15 and
  // 19-29 of the middle one: the document code, the issuing state, the sex and
  // the nationality are all outside it. That is why an issuing state can be
  // changed in a zone without any digit noticing.
  const compositeOver = `${documentNumber}${documentCheck}${optional}${middle.slice(0, 7)}${middle.slice(8, 15)}${middle.slice(18, 29)}`;
  const compositeCheck = digitFor(compositeOver);

  const line2 = fill(`${middle}${compositeCheck}`, TD1_LINE_LENGTH);
  const line3 = fill(nameZone(input.surname, input.givenNames), TD1_LINE_LENGTH);

  return { line1, line2, line3 };
}
